import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { Child } from "../types";
import VoxelBackground from "../components/VoxelBackground";

interface ProgressItem {
  id: string;
  completed: boolean;
  score: number;
  attempts: number;
  completedAt: string;
  lesson: {
    title: string;
    subject: string;
    grade: number;
  };
}

export default function Progress() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState<Child | null>(null);
  const [progress, setProgress] = useState<ProgressItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get("/children");
      const found = res.data.children.find((c: any) => c.id === id);
      setChild(found);
      setProgress(found?.progress?.filter((p: any) => p.lesson) || []);
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={styles.loading}>⏳ Ładowanie...</div>;

  const completed = progress.filter((p) => p.completed).length;

  return (
    <div style={styles.container}>
      <VoxelBackground />
      <div style={styles.header}>
        <button onClick={() => navigate("/dashboard")} style={styles.backBtn}>
          ← Wróć
        </button>
        <h1 style={styles.logo}>📊 Postępy</h1>
        <div />
      </div>

      <div style={styles.content}>
        <div style={styles.childInfo}>
          <div style={styles.avatar}>🤖</div>
          <h2 style={styles.childName}>{child?.name}</h2>
          <p style={styles.childLevel}>Poziom {child?.level}</p>
        </div>

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>⭐ {child?.points}</div>
            <div style={styles.statLabel}>Punkty łącznie</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>✅ {completed}</div>
            <div style={styles.statLabel}>Lekcji ukończonych</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>🎯 {progress.length}</div>
            <div style={styles.statLabel}>Prób łącznie</div>
          </div>
        </div>

        <h3 style={styles.sectionTitle}>Historia lekcji</h3>

        {progress.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: "48px" }}>📚</div>
            <p>Brak ukończonych lekcji — czas zacząć!</p>
          </div>
        ) : (
          <div style={styles.progressList}>
            {progress.map((item) => (
              <div key={item.id} style={styles.progressItem}>
                <div style={styles.progressIcon}>
                  {item.lesson?.subject === "MATH" ? "🧮" : "💻"}
                </div>
                <div style={styles.progressInfo}>
                  <h4 style={styles.progressTitle}>{item.lesson?.title}</h4>
                  <p style={styles.progressMeta}>
                    Klasa {item.lesson?.grade} • {item.attempts} prób
                  </p>
                </div>
                <div style={styles.progressScore}>
                  <div style={styles.scoreNumber}>+{item.score} pkt</div>
                  <div style={{
                    ...styles.statusBadge,
                    background: item.completed ? "#e8f5e9" : "#fff3cd",
                    color: item.completed ? "#2e7d32" : "#f57f17",
                  }}>
                    {item.completed ? "✅ Ukończona" : "🔄 W toku"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    background: "#87CEEB",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "16px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 2,
  },
  backBtn: {
    background: "rgba(255,255,255,0.2)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: "16px",
  },
  logo: { color: "white", margin: 0, fontSize: "24px" },
  content: {
    padding: "32px",
    maxWidth: "800px",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
  },
  childInfo: { textAlign: "center", marginBottom: "32px" },
  avatar: { fontSize: "80px" },
  childName: { fontSize: "28px", color: "#333", margin: "8px 0" },
  childLevel: { color: "#888", fontSize: "16px" },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    marginBottom: "32px",
  },
  statCard: {
    background: "white",
    borderRadius: "16px",
    padding: "24px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  statNumber: { fontSize: "28px", fontWeight: "bold", marginBottom: "8px" },
  statLabel: { color: "#888", fontSize: "14px" },
  sectionTitle: { fontSize: "20px", color: "#333", marginBottom: "16px" },
  progressList: { display: "flex", flexDirection: "column", gap: "12px" },
  progressItem: {
    background: "white",
    borderRadius: "16px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  progressIcon: { fontSize: "32px" },
  progressInfo: { flex: 1 },
  progressTitle: { margin: "0 0 4px", fontSize: "16px", color: "#333" },
  progressMeta: { margin: 0, color: "#888", fontSize: "14px" },
  progressScore: { textAlign: "right" as const },
  scoreNumber: { fontSize: "18px", fontWeight: "bold", color: "#333", marginBottom: "4px" },
  statusBadge: {
    borderRadius: "20px",
    padding: "4px 12px",
    fontSize: "12px",
    display: "inline-block",
  },
  emptyState: {
    textAlign: "center",
    padding: "48px",
    color: "#888",
    fontSize: "18px",
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    fontSize: "24px",
  },
};